import { useEffect, useMemo, useState } from "react";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import PhoneNumbersRenderCell from "@/DataPages/components/common/PhoneNumbersRenderCell";
import EmailsRenderCell from "@/DataPages/components/common/EmailsRenderCell";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// MUI
import { Circle } from "@mui/icons-material";
import { Box, Fade, Stack, SxProps } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";

// Types
import { IMailDetails, IPhoneDetails } from "@/DataPages/types/rci/salesLeads/model";
import { ITitling } from "@/DataPages/types/titling/model";
import { FnI_Status } from "@/Common/hooks/SmartTableColGenerator/types";

// Helpers
import { generateSx, getPinnedCellClassName, getRowClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { tableKeys } from "@/Constants/tableKeys";
import { formatPhoneNumber } from "@/Common/helpers/Modifiers/formatters";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Icons
import { getCellClassNameFn } from "@/Common/helpers/Tables";

// Constants
import { COLUMN_RESIZE_FACTOR, HEADER_CLASS_NAME, TWO_HUNDRED } from "@/Constants/tables";
import { DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

const CELL_CLASS_NAME = "table-small-cell";

const ONE_HUNDRED_THIRTY = 130;
const pinnedColumns = { left: ["lastName", "saleDate", "vinNumber", "stockNumber", "dealNumber", "status", "__empty"] };

const baseColumnDefinition: Partial<GridColDef> = {
	headerClassName: HEADER_CLASS_NAME,
	renderCell: (params) => <TypographyCell params={params} />,
	renderHeader: (params) => <TypographyHeader params={params} />,
};

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		width: 10,
		filterable: false,
	},
	{
		...baseColumnDefinition,
		field: "lastName",
		headerName: "Finance Employee",
		minWidth: 100,
		width: 100,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		valueGetter: (_, row) => row?.financeManager?.lastName,
	},
	{
		...baseColumnDefinition,
		field: "saleDate",
		headerName: "Sale Date",
		type: "date",
		width: 80,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param),
		valueGetter: (value) => value && getDateInUTC(value),
		valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
	},
	{
		...baseColumnDefinition,
		field: "vinNumber",
		headerName: "VIN",
		minWidth: 120,
		width: 120,
		cellClassName: (params) => getPinnedCellClassName(params, CELL_CLASS_NAME),
	},
	{
		...baseColumnDefinition,
		field: "stockNumber",
		headerName: "Stock#",
		minWidth: 70,
		width: 70,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
	},
	{
		...baseColumnDefinition,
		field: "dealNumber",
		headerName: "Deal#",
		minWidth: 70,
		width: 70,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
	},
	{
		...baseColumnDefinition,
		field: "status",
		headerName: "Status",
		minWidth: 70,
		width: 70,
		headerClassName: HEADER_CLASS_NAME,
		resizable: false,
		cellClassName: (param) => getPinnedCellClassName(param, CELL_CLASS_NAME),
		renderCell: (params) => {
			return (
				<Stack direction="row" spacing={1}>
					<Circle className={`!w-[10px] !text-${FnI_Status.getFIClosedStatusColor(params.value)}`} />
					<TypographyCell params={params} />
				</Stack>
			);
		},
	},
	{
		...baseColumnDefinition,
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		minWidth: 12,
		width: 12,
		maxWidth: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
	},
	{
		...baseColumnDefinition,
		field: "titlingNumber",
		headerName: "Titling No",
		minWidth: 70,
		width: 70,
		resizable: false,
		align: "right",
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.dealer?.titlingNumber,
	},
	{
		...baseColumnDefinition,
		field: "taxId",
		headerName: "Tax ID",
		minWidth: 70,
		width: 70,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.dealer?.taxId,
	},
	{
		...baseColumnDefinition,
		field: "dealerName",
		headerName: "Dealer Name",
		minWidth: 130,
		width: 130,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.dealer?.contactInfo?.dealerName,
	},
	{
		...baseColumnDefinition,
		field: "coOwner",
		headerName: "Co Owner",
		minWidth: 70,
		width: 70,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.hasCoOwner,
		valueFormatter: (value) => (value ? "Y" : "N"),
	},
	{
		...baseColumnDefinition,
		field: "outOfStateSale",
		headerName: "Out of State Sale",
		minWidth: ONE_HUNDRED_THIRTY,
		width: ONE_HUNDRED_THIRTY,
		maxWidth: ONE_HUNDRED_THIRTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		resizable: false,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.isOutOfStateSale,
		valueFormatter: (value) => (value ? "Y" : "N"),
	},
	{
		...baseColumnDefinition,
		field: "asIs",
		headerName: "As Is",
		minWidth: 40,
		width: 40,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.isSold,
		valueFormatter: (value) => (value ? "Y" : "N"),
	},
	{
		...baseColumnDefinition,
		field: "unitOfMeasurement",
		headerName: "Units of Measure",
		minWidth: 80,
		width: 80,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.unitOfMeasurement,
	},
	{
		...baseColumnDefinition,
		field: "fleetDeal",
		headerName: "Fleet Deal",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.isFleetDeal,
		valueFormatter: (value) => (value ? "Y" : "N"),
	},
	{
		...baseColumnDefinition,
		field: "select1",
		headerName: "Select 1",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.select1,
	},
	{
		...baseColumnDefinition,
		field: "select2",
		headerName: "Select 2",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.select2,
	},
	{
		...baseColumnDefinition,
		field: "select3",
		headerName: "Select 3",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.select3,
	},
	{
		...baseColumnDefinition,
		field: "ros",
		headerName: "ROS",
		minWidth: 50,
		width: 50,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.miscellaneousInformation?.salesNumberReceipt,
	},
	{
		...baseColumnDefinition,
		field: "custLastName",
		headerName: "Cust. Last Name",
		minWidth: 80,
		width: 80,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.lastName,
	},
	{
		...baseColumnDefinition,
		field: "custMidName",
		headerName: "Cust. Mid Name",
		minWidth: 80,
		width: 80,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.midName,
	},
	{
		...baseColumnDefinition,
		field: "custFirstName",
		headerName: "Cust. First Name",
		minWidth: 85,
		width: 85,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.firstName,
	},
	{
		...baseColumnDefinition,
		field: "nameRecId",
		headerName: "Name Rec ID",
		minWidth: 80,
		width: 80,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.customerNumber,
	},
	{
		...baseColumnDefinition,
		field: "ibFlag",
		headerName: "IB Flag",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.ibFlag,
	},
	{
		...baseColumnDefinition,
		field: "zip",
		headerName: "ZIP",
		minWidth: 60,
		width: 60,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.contactInfo?.address?.zipCode,
	},
	{
		...baseColumnDefinition,
		field: "phoneNumbers",
		headerName: "Phone",
		minWidth: TWO_HUNDRED,
		width: TWO_HUNDRED,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => {
			const value = row?.buyer?.customerRecord?.contactInfo?.phone;
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IPhoneDetails[]).map((phone) => {
				const type = phone?.type ? `(${phone?.type})` : "";
				const formattedPhone = formatPhoneNumber(phone?.number, false);
				const ext = phone?.extension ? `ext. ${phone?.extension}` : "";
				return `${type} ${formattedPhone} ${ext}`;
			});
		},
		renderCell: (params) => <PhoneNumbersRenderCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "mailTo",
		headerName: "Mail To",
		minWidth: TWO_HUNDRED,
		width: TWO_HUNDRED,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => {
			const value = row?.buyer?.customerRecord?.contactInfo?.email;
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IMailDetails[]).map((emailItem) => {
				return emailItem?.mailTo;
			});
		},
		renderCell: (params) => {
			return <EmailsRenderCell params={params} />;
		},
	},
	{
		...baseColumnDefinition,
		field: "followUpType",
		headerName: "Follow Up Type",
		minWidth: 75,
		width: 75,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.dmsCustomerInfo?.followUp?.followUpType,
	},
	{
		...baseColumnDefinition,
		field: "value",
		headerName: "Value",
		minWidth: 75,
		width: 75,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.buyer?.customerRecord?.dmsCustomerInfo?.followUp?.hasFollowUp,
		valueFormatter: (value) => (value ? "Y" : "N"),
	},
	{
		...baseColumnDefinition,
		field: "coLastName",
		headerName: "Co Last Name",
		minWidth: 75,
		width: 75,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.lastName,
	},
	{
		...baseColumnDefinition,
		field: "coMidName",
		headerName: "Co Mid Name",
		minWidth: 75,
		width: 75,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.midName,
	},
	{
		...baseColumnDefinition,
		field: "coFirstName",
		headerName: "Co First Name",
		minWidth: 75,
		width: 75,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.firstName,
	},
	{
		...baseColumnDefinition,
		field: "coNameRecId",
		headerName: "Co Name Rec ID",
		minWidth: 80,
		width: 80,
		resizable: true,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.customerNumber,
	},
	{
		...baseColumnDefinition,
		field: "coIbFlag",
		headerName: "Co IB Flag",
		minWidth: 75,
		width: 75,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.ibFlag,
	},
	{
		...baseColumnDefinition,
		field: "coSuffix",
		headerName: "Co Suffix",
		minWidth: 75,
		width: 75,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.suffix,
	},
	{
		...baseColumnDefinition,
		field: "coZip",
		headerName: "Co Zip",
		minWidth: 75,
		width: 75,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => row?.coBuyer?.customerRecord?.contactInfo?.address?.zipCode,
	},
	{
		...baseColumnDefinition,
		field: "coPhoneNumbers",
		headerName: "Co Phones",
		minWidth: TWO_HUNDRED,
		width: TWO_HUNDRED,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => {
			const value = row?.coBuyer?.customerRecord?.contactInfo?.phone;
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IPhoneDetails[]).map((phone) => {
				const type = phone?.type ? `(${phone?.type})` : "";
				const formattedPhone = formatPhoneNumber(phone?.number, false);
				const ext = phone?.extension ? `ext. ${phone?.extension}` : "";
				return `${type} ${formattedPhone} ${ext}`;
			});
		},
		renderCell: (params) => <PhoneNumbersRenderCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "coMailTo",
		headerName: "Co Mail To",
		minWidth: TWO_HUNDRED,
		width: TWO_HUNDRED,
		resizable: false,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn([], {}, {}),
		valueGetter: (_, row) => {
			const value = row?.coBuyer?.customerRecord?.contactInfo?.email;
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IMailDetails[]).map((emailItem) => {
				return emailItem?.mailTo;
			});
		},
		renderCell: (params) => {
			return <EmailsRenderCell params={params} />;
		},
	},
];

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
};

const TableCustomComponents = ({ isPaginating }: { isPaginating: boolean }) => {
	return (
		<>
			{isPaginating && (
				<Fade in={isPaginating} timeout={400}>
					<Box className="mt-4">
						<LoadingSpinner label="Titlings data is still loading..." className="items-center" />
					</Box>
				</Fade>
			)}
		</>
	);
};

interface ITitlingTableProps {
	data: ITitling[];
	isPaginating: boolean;
}

export default function TitlingTable(props: Readonly<ITitlingTableProps>) {
	const { data, isPaginating } = props;
	const apiRef = useGridApiRef();

	const slotProps = useMemo(
		() => ({
			toolbar: {
				customComponent: TableCustomComponents({ isPaginating }),
				titleExpandible: {
					className: "[&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
					hide: true,
				},
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
			},
			noRowsOverlay: {
				title: "No Titling Data",
			},
		}),
		[data]
	);

	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		setSx(generateSx(apiRef));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [props, apiRef]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.FNI_MANAGEMENT_FI_TITLING,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={props.data}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			rowHeight={DEFAULT_ROW_HEIGHT}
			maxRows={19}
			pinnedColumns={pinnedColumns}
			getRowClassName={getRowClassName}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={slots}
			slotProps={slotProps}
			hideFooter
			customProps={{
				tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
				tableClassName: {
					"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
					"[&_.MuiDataGrid-row:not(.empty-row):hover_.MuiDataGrid-cell:not(.empty-row)]:shadow-table-hov": true,
				},
				expandableRows: true,
				hide: false,
			}}
			sx={sx}
		/>
	);
}
